/**
 * Plans v2 local caps for API data (collections, requests, environments),
 * counted across ALL the user's own LOCAL workspaces — the open one from the
 * live stores, every other one from its snapshot — the way boards and
 * documents count across scopes. Free's "2 collections" are 2 on this
 * machine, not 2 per workspace (an account over the cap keeps every
 * workspace it already had, so per-workspace counting let it make 2 more in
 * each).
 *
 * The caps are about LOCAL data, so they apply where the item would be
 * created on this machine: an own local (or git) workspace. A team
 * workspace's data is the team's; an own CLOUD workspace is refused as a
 * whole while the cloud is read-only (plans/localGate.requireWritableWorkspace),
 * and with `cloud_sync` the plan caps nothing locally anyway.
 *
 * UI only: it reaches the workspace store router (filesystem, git). The MCP
 * write path gets the other workspaces' counts through the slot in
 * ./otherLocalApiCounts, which this module fills on import.
 */
import { useAuthStore } from '../store/authStore';
import { useWorkspaceStore } from '../store/workspaceStore';
import { useCollectionsStore } from '../store/collectionsStore';
import { useVariablesStore } from '../store/variablesStore';
import { isLocalWorkspace } from '../utils/workspaceStorage';
import { isTeamWorkspace } from '../utils/workspaceShareEligibility';
import { countApiItems } from './apiCounts';
import { capRefusal, isCapped } from './capRefusal';
import { requireLocalCapacity } from './localGate';
import { NO_API_ITEMS, otherLocalApiCounts, registerOtherLocalApiCounter } from './otherLocalApiCounts';

export const API_KINDS = Object.freeze(['collections', 'requests', 'environments']);

const authed = () => Boolean(useAuthStore.getState?.().isAuthenticated);
const sameWorkspace = (a, b) => a != null && b != null && String(a.id) === String(b.id);

const isOwnLocal = (ws) => Boolean(ws) && isLocalWorkspace(ws, authed()) && !isTeamWorkspace(ws);

/**
 * Do the local API caps apply to items created in `workspace` (default: the
 * open one)? Yes in an own local workspace, and with no workspace at all (the
 * open store is all there is). No in a team or cloud workspace.
 */
export function apiCapsApply(workspace = useWorkspaceStore.getState().currentWorkspace) {
  return !workspace || isOwnLocal(workspace);
}

const countsOf = (collections, environments) => ({
  ...countApiItems(collections),
  environments: Array.isArray(environments) ? environments.length : 0,
});

const add = (a, b) => ({
  collections: a.collections + b.collections,
  requests: a.requests + b.requests,
  environments: a.environments + b.environments,
});

/**
 * The own local workspaces OTHER than the open one (and than `exclude`, an
 * id), from their snapshots. A workspace that cannot be read counts as empty:
 * a git workspace whose folder is gone must not block every create for good.
 */
export async function countOtherOwnLocalApi({ exclude } = {}) {
  const { workspaces, currentWorkspace } = useWorkspaceStore.getState();
  const others = (Array.isArray(workspaces) ? workspaces : []).filter(
    (ws) =>
      isOwnLocal(ws) &&
      !sameWorkspace(ws, currentWorkspace) &&
      (exclude == null || String(ws.id) !== String(exclude))
  );
  let total = { ...NO_API_ITEMS };
  if (!others.length) return total;
  // Lazy: the router reaches the sync store, which imports @tauri-apps/api
  // (50-gotchas: shared code must not import it statically).
  const { loadSnapshotById } = await import('../services/workspaceStoreRouter');
  for (const ws of others) {
    try {
      const snap = await loadSnapshotById(workspaces, ws.id);
      total = add(total, countsOf(snap?.collections, snap?.environments));
    } catch {
      // unreadable: counts as empty (see above)
    }
  }
  return total;
}

registerOtherLocalApiCounter(() => countOtherOwnLocalApi());

/** What the open workspace holds, when it is an own local one (else nothing local). */
function openWorkspaceCounts() {
  if (!apiCapsApply()) return { ...NO_API_ITEMS };
  return countsOf(useCollectionsStore.getState().collections, useVariablesStore.getState().environments);
}

/**
 * `{ collections, requests, environments }` across every own local
 * workspace. `opts.exclude` (a workspace id) leaves one out, open or not: a
 * caller that writes a workspace's snapshot directly counts that snapshot
 * itself.
 */
export async function countOwnLocalApi(opts = {}) {
  const open = useWorkspaceStore.getState().currentWorkspace;
  const excludeOpen = opts.exclude != null && open != null && String(open.id) === String(opts.exclude);
  const here = excludeOpen ? { ...NO_API_ITEMS } : openWorkspaceCounts();
  const others = opts.exclude == null ? await otherLocalApiCounts() : await countOtherOwnLocalApi(opts);
  return add(here, others);
}

/**
 * What is in use of `kind`. With `target` ({ id, collections, environments }:
 * a local workspace's snapshot a caller is about to write onto, open or not),
 * that workspace counts by the snapshot and everything else as usual.
 */
async function usedOf(kind, target) {
  if (!target) return (await countOwnLocalApi())[kind];
  const rest = await countOwnLocalApi({ exclude: target.id });
  return rest[kind] + countsOf(target.collections, target.environments)[kind];
}

/**
 * The UI gate for API items: may `adding` (default 1) more `kind` be created
 * in the open workspace? False after opening the upgrade with the cap
 * sentence. Counts only when the plan caps `kind` and the caps apply to the
 * open workspace. `opts.target` (see usedOf) writes into a LOCAL workspace
 * instead, which always counts.
 */
export async function requireApiCapacity(kind, adding = 1, opts = {}) {
  if (!isCapped(kind)) return true;
  if (!opts.target && !apiCapsApply()) return true;
  return requireLocalCapacity(kind, await usedOf(kind, opts.target), { adding });
}

/** Same for several kinds at once (`{ collections: 1, requests: 6 }`); stops at the first refusal. */
export async function requireApiRoom(adding, opts = {}) {
  for (const kind of API_KINDS) {
    const n = Number(adding?.[kind]) || 0;
    if (n > 0 && !(await requireApiCapacity(kind, n, opts))) return false;
  }
  return true;
}

/** The first refusal SENTENCE for `adding` (as requireApiRoom), or null when it all fits. Opens nothing. */
export async function apiRoomRefusal(adding, opts = {}) {
  for (const kind of API_KINDS) {
    const n = Number(adding?.[kind]) || 0;
    if (n > 0 && isCapped(kind) && (opts.target || apiCapsApply())) {
      const refused = capRefusal(kind, await usedOf(kind, opts.target), n);
      if (refused) return refused;
    }
  }
  return null;
}

/**
 * The refusal SENTENCE (no modal: the in-app AI), or null when `adding` more
 * `kind` fit in the open workspace.
 */
export async function apiCapRefusal(kind, adding = 1) {
  if (!isCapped(kind) || !apiCapsApply()) return null;
  return capRefusal(kind, (await countOwnLocalApi())[kind], adding);
}
